import { v4 as uuidV4, validate as uuidValidate, version as uuidVersion } from 'uuid'
import { ExclusionAreaGeoJSON, RefData } from '../../../types/apiModels'
import { EXCLUSION_AREA_TYPE_IDS } from '../../../utils/constants'
import { GeoJSON } from '../../../utils/GeoJSON/index'

const IMPORTED_AREA_COLOR = '#2196F3'

const resolveCategoryName = (
  feature: GeoJSON.Feature,
  exclusionAreaTypes: RefData[] | undefined,
): string => {
  if (!feature.properties?.exclusionAreaTypeId) return feature.properties?.name || ''

  return (
    exclusionAreaTypes?.find(type => type.id.toString() === feature.properties.exclusionAreaTypeId)
      ?.es_AR || ''
  )
}

// Only ids the backend minted are reused; anything else (a hand-edited KML, a GIS export) gets a
// client-side tmpId so the area is staged as new instead of overwriting an unrelated record.
const reusableId = (id: unknown): string | undefined =>
  typeof id === 'string' && uuidValidate(id) && uuidVersion(id) === 4 ? id : undefined

/**
 * Turns a KML feature parsed by getFeaturesFromKML into a staged exclusion area.
 *
 * The parser rewrites `properties.area` as a display string in whichever unit fits the geometry
 * ("2819.25 m²" below one hectare), while saved areas carry a hectare number. The area is
 * recomputed here so both lists render through formattedHectaresArea in the same unit.
 */
export const toImportedExclusionArea = (
  feature: GeoJSON.Feature,
  { farmId, exclusionAreaTypes }: { farmId: string; exclusionAreaTypes: RefData[] | undefined },
): ExclusionAreaGeoJSON => {
  const { properties } = feature
  const exclusionAreaTypeId =
    properties.exclusionAreaTypeId &&
    properties.exclusionAreaTypeId !== EXCLUSION_AREA_TYPE_IDS.OTHER
      ? Number(properties.exclusionAreaTypeId)
      : null

  return {
    ...feature,
    properties: {
      ...properties,
      id: reusableId(properties.id),
      tmpId: uuidV4(),
      farmId,
      type: 'exclusion_area',
      area: GeoJSON.hectareArea(feature),
      exclusionAreaTypeId,
      name: resolveCategoryName(feature, exclusionAreaTypes),
      otherName: properties.otherName || properties.name || '',
      hasGrazingManagement: String(properties.hasGrazingManagement) === 'true',
      color: IMPORTED_AREA_COLOR,
      isImported: true,
    },
  } as unknown as ExclusionAreaGeoJSON
}
